import { useState } from 'react';
import { notification } from 'antd';
import FloatingModal from '../FloatingModal/FloatingModal';
import { createNps } from '../../API/services/screeningService';
import { t } from '../../i18n/i18n';
import './NPSModal.scss';
import { useNPSModal } from '../../hooks/useNPSModal';
import { getLocalStorageWorkspace } from '../../utils/workspaceUtils';

const NPSModal = ({ onSuccess, onError, title = 'Evaluación de Experiencia' }) => {
  const {
    isNPSOpen,
    npsProcessType,
    closeNPSModal,
    setCooldown,
    EMOJI_RATINGS,
    RATING_LABELS,
    RATING_COLORS,
    PLATFORM,
  } = useNPSModal();

  const [score, setScore] = useState(0);
  const [comment, setComment] = useState('');
  const [hoveredScore, setHoveredScore] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = () => {
    setScore(0);
    setComment('');
    setHoveredScore(0);
    closeNPSModal();
  };

  const handleSubmit = async () => {
    if (score === 0) return;
    const { companyId } = getLocalStorageWorkspace();

    setIsSubmitting(true);

    try {
      const npsData = {
        score,
        comment: comment.trim() || undefined,
        processType: npsProcessType,
        platform: PLATFORM,
        companyId,
      };

      await createNps(npsData);
      setCooldown();

      notification.success({
        message: t('NPS_SUCCESS_TITLE'),
        description: t('NPS_SUCCESS_MESSAGE'),
        placement: 'topRight',
        duration: 5,
      });

      if (onSuccess) {
        onSuccess(npsData);
      }

      handleClose();
    } catch (error) {
      notification.error({
        message: t('NPS_ERROR_TITLE', 'Error al enviar evaluación'),
        description: t(
          'NPS_ERROR_MESSAGE',
          'No pudimos enviar tu evaluación. Por favor, inténtalo de nuevo.'
        ),
        placement: 'topRight',
        duration: 5,
      });

      if (onError) {
        onError(error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayScore = hoveredScore || score;

  return (
    <FloatingModal
      show={isNPSOpen}
      onClose={handleClose}
      title={title}
      className="nps-floating-modal"
    >
      <div className="nps-modal-content">
        <div className="nps-modal-header">
          <h3 className="nps-modal-title">{t('NPS_MODAL_TITLE')}</h3>
          <p className="nps-modal-subtitle">{t('NPS_MODAL_SUBTITLE')}</p>
        </div>

        <div className="nps-rating-section">
          <div className="emoji-rating-container">
            {[1, 2, 3, 4, 5].map((rating) => (
              <button
                type="button"
                key={rating}
                className={`emoji-rating-button ${
                  (hoveredScore || score) >= rating ? 'active' : ''
                }`}
                onClick={() => setScore(score === rating ? 0 : rating)}
                onMouseEnter={() => setHoveredScore(rating)}
                onMouseLeave={() => setHoveredScore(0)}
                disabled={isSubmitting}
                style={{
                  borderColor: (hoveredScore || score) >= rating ? RATING_COLORS[rating] : '#ddd',
                  backgroundColor:
                    (hoveredScore || score) >= rating
                      ? `${RATING_COLORS[rating]}20`
                      : 'transparent',
                  color: (hoveredScore || score) >= rating ? RATING_COLORS[rating] : '#666',
                }}
              >
                <span className="emoji">{EMOJI_RATINGS[rating]}</span>
                <span className="rating-number">{rating}</span>
              </button>
            ))}
          </div>

          <div
            className="rating-label"
            style={{
              color: RATING_COLORS[displayScore],
            }}
          >
            {t(RATING_LABELS[displayScore])}
          </div>
        </div>

        <div className="nps-comment-section">
          <label htmlFor="nps-comment" className="comment-label">
            {t('NPS_MODAL_COMMENT_LABEL', 'Cuéntanos más (opcional)')}
          </label>
          <textarea
            id="nps-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t('NPS_MODAL_COMMENT_PLACEHOLDER')}
            className="comment-textarea"
            rows="3"
            maxLength="500"
            disabled={isSubmitting}
          />
          <div className="comment-counter">{comment.length}/500</div>
        </div>

        <div className="nps-modal-actions">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 modal-buttons-border bg-transparent border-red-danger text-red-danger outline-none font-weight-700 mr-2"
            disabled={isSubmitting}
          >
            {t('CANCEL')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="px-4 py-2 modal-buttons-border border-0 text-white outline-none font-weight-700 ml-2"
            style={{ backgroundColor: score === 0 ? '#ccc' : '#449cc8' }}
            disabled={score === 0 || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2" />
                {t('SUBMITTING')}
              </>
            ) : (
              t('SUBMIT')
            )}
          </button>
        </div>
      </div>
    </FloatingModal>
  );
};

export default NPSModal;
